import React, { useState } from "react";
import LiveNotifications from "./LiveNotifications";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Menu from "./Menu";
import PlanExpireBanner from "src/components/dashboard/PlanExpireBanner";

interface LayoutProps {
  children: React.ReactNode;
  type?: "admin" | "tenant";
  meta?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
}

const SIDEBAR_COLLAPSED_KEY = "sidebar_collapsed";

const getInitialCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const Layout: React.FC<LayoutProps> = ({ children, type = "tenant" }) => {
  const [collapsedState, setCollapsed] = useState<boolean>(getInitialCollapsed);
  // Collapse is a tenant-shell feature only — admin's Topbar never renders
  // the toggle, so its sidebar must never silently collapse via a stale
  // localStorage flag left over from the tenant shell.
  const collapsed = type === "tenant" && collapsedState;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore write failures
      }
      return next;
    });
  };

  const expand = () => {
    setCollapsed(false);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, "0");
    } catch {
      // ignore write failures
    }
  };

  return (
    <div className="h-screen flex flex-col dark:bg-gray-950">
      <LiveNotifications type={type}>
      {/* Top bar */}
      <Topbar
        type={type}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
      <PlanExpireBanner type={type} />

      {/* Main content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside
          className={`hidden md:block bg-white border-r border-gray-300 transition-all duration-200 dark:bg-gray-900 dark:border-gray-700 ${
            collapsed ? "w-16" : "w-64"
          }`}
        >
          <Menu type={type} collapsed={collapsed} onRequestExpand={expand} />
        </aside>
        <main className="flex-1 min-h-0 overflow-y-scroll flex flex-col justify-between dark:bg-gray-950">
          <div className="p-4">{children}</div>

          {/* Footer */}
          <Footer />
        </main>
      </div>
      </LiveNotifications>
    </div>
  );
};

export default Layout;
